import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

import { RootState, AppThunk, AppDispatch } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import {
  FileType,
  FileStatus,
  ProjectFile,
  SetFileConfig,
  removeFile as removeFileServ,
  setFileAccessedAt,
  getProjectFiles,
  setFilePoster,
  SetFileMediaOriginDateBody,
  setFileMediaOriginDate,
  uploadFilePoster as uploadImage,
  uploadTextImage,
  UploadTextImage,
} from 'services/file.service';
import { BackupReq, BackupHandlers, addBackup } from 'components/app/editor/_mediator/backup/backup.slice';
import { createBackup } from 'components/app/editor/_mediator/backup/backup.helper';
import { Selector, handleClips } from 'components/app/editor/_mediator/editor.slice';

const SLICE_ID = 'app__files';
const ERR_ID = 'app-files.slice';

export type FileConfigsMap = Record<FileId, SetFileConfig>;
export type FilesMap = Record<FileId, ProjectFile>;
export type FilesStatusesMap = Record<FileId, FileStatus>;
export type PosterUrlsMap = Record<FileId, Url | null>;

interface UploadFilePosterParams {
  projectId: ProjectId;
  fileId: FileId;
  blob: Blob;
}

interface UploadTitleCreditsImage {
  payload: UploadTextImage;
  blob: Blob;
}

interface AddNewFile {
  file: ProjectFile;
  projectId: ProjectId;
}

interface State {
  files: FilesMap;
  configs: FileConfigsMap;
  statuses: FilesStatusesMap;
  posters: PosterUrlsMap;
}

const initialState: State = {
  files: {},
  statuses: {},
  configs: {},
  posters: {},
};

// S E L E C T O R S

export const selectFiles = createSelector(
  (state: RootState) => state.app__files.files,
  (files) =>
    Object.values(files).reduce(
      (result, file) => {
        switch (file.fileType) {
          case FileType.audio:
            result.audios.push(file);
            if (file.status === FileStatus.Available) result.isAudio = true;
            break;
          case FileType.video:
            result.videos.push(file);
            if (file.status === FileStatus.Available) result.isVideo = true;
            break;
          case FileType.image:
            result.images.push(file);
            if (file.status === FileStatus.Available) result.isImage = true;
            break;
        }
        if (file.duration === 0 || file.size === 0) result.brokenFiles.push(file);
        return result;
      },
      {
        audios: [] as ProjectFile[],
        videos: [] as ProjectFile[],
        images: [] as ProjectFile[],
        brokenFiles: [] as ProjectFile[],
        isAudio: false,
        isVideo: false,
        isImage: false,
      }
    )
);

export const selectSelectedFile = createSelector(
  (state: RootState) => state.app__files.files,
  (state: RootState) => state.projects.selectedFile,
  (files, selectedFile) => {
    if (!files || !selectedFile) return;
    const fileData = files[selectedFile];
    if (fileData) return fileData;
    else console.error(`[${ERR_ID}] selectSelectedFile: file data not found`);
  }
);

// T H U N K S

export const handleFileConfig =
  (payload: BackupReq<BackupHandlers.handleFileConfigs>): AppThunk =>
  async (dispatch, getState) => {
    const configs = getState().app__files.configs;
    const backupData = configs?.[payload.data.fileId];

    if (!backupData) {
      console.error(`[${ERR_ID}] handleFileConfig: backupData not found`);
      return;
    }

    const backup = createBackup(BackupHandlers.handleFileConfigs, payload, backupData);
    dispatch(addBackup(backup, payload));
    dispatch(slice.actions.updateConfigs(payload.data));
  };
//

export const updFileUrls = createSafeAsyncThunk(SLICE_ID + '/updFileUrls', async (projectId: number) => {
  const res = await getProjectFiles(projectId);
  return res.data;
});

export const fetchFiles = createSafeAsyncThunk(
  SLICE_ID + '/fetchFiles',
  async (projectId: number, { getState, dispatch }) => {
    const { selector } = (getState() as RootState).editor;
    const files = (await getProjectFiles(projectId)).data;

    const filesMap: FilesMap = {};
    const configs: FileConfigsMap = {};
    const newSelectorFiles: Selector = {};
    const statuses: FilesStatusesMap = {};
    const posters: PosterUrlsMap = {};

    files.forEach((file) => {
      filesMap[file.id] = file;
      statuses[file.id] = file.status;
      if (!selector || !selector[file.id]) newSelectorFiles[file.id] = [];
      if (file.fileType === FileType.video || file.fileType === FileType.image) {
        configs[file.id] = {
          projectId,
          fileId: file.id,
          ...file.config,
        };
        posters[file.id] = file.fileWithEffectsUrls.posterUrl;
      }
    });
    (dispatch as AppDispatch)(handleClips({ data: { selector: newSelectorFiles }, backupSkip: true }));
    return { data: filesMap, configs, posters, statuses };
  }
);

export const putFile = createSafeAsyncThunk(
  SLICE_ID + '/putFile',
  async (payload: AddNewFile, { getState, dispatch }) => {
    const { selector } = (getState() as RootState).editor;
    const newSelector = { ...selector };
    const { file, projectId } = payload;

    const config: SetFileConfig = {
      projectId,
      fileId: file.id,
      ...file.config,
    };
    const poster: Url | null = file.fileWithEffectsUrls.posterUrl;
    const status = file.status;
    newSelector[file.id] ??= [];

    (dispatch as AppDispatch)(handleClips({ data: { selector: newSelector }, backupSkip: true }));
    return { file, config, poster, status };
  }
);

export const uploadFilePoster = createSafeAsyncThunk(
  SLICE_ID + '/uploadFilePoster',
  async (params: UploadFilePosterParams) => {
    const { projectId, blob, fileId } = params;
    const posterKey = await uploadImage({ projectId, key: fileId.toString() }, blob);
    const res = await setFilePoster({ fileId, posterKey });
    return { fileId, posterURL: res.data.data };
  }
);

export const uploadTitleCreditsImage = createSafeAsyncThunk(
  SLICE_ID + '/uploadTitleCreditsImage',
  async (params: UploadTitleCreditsImage) => {
    await uploadTextImage(params.payload, params.blob);
  }
);

export const removeFile = createSafeAsyncThunk(SLICE_ID + '/removeFile', async (fileId: number) => {
  const response = await removeFileServ(fileId);
  return response.data;
});

export const passFileAccessedAt = createSafeAsyncThunk(SLICE_ID + '/passFileAccessedAt', async (fileId: FileId) => {
  const response = await setFileAccessedAt(fileId);
  return response.data;
});

export const updateFileMediaOriginDate = createSafeAsyncThunk(
  SLICE_ID + '/updateFileMediaOriginDate',
  async (params: SetFileMediaOriginDateBody) => {
    const response = await setFileMediaOriginDate(params);
    return response.data;
  }
);

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    updateConfigs: (state, action: PayloadAction<SetFileConfig>) => {
      state.configs[action.payload.fileId] = action.payload;
    },
    updateFileStatus: (state, action: PayloadAction<{ fileId: FileId; status: FileStatus }>) => {
      const { fileId, status } = action.payload;
      state.statuses[fileId] = status;
    },
    resetFiles: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFiles.fulfilled, (state, action) => {
      state.files = action.payload.data;
      state.configs = action.payload.configs;
      state.statuses = action.payload.statuses;
      state.posters = action.payload.posters;
    });
    builder.addCase(putFile.fulfilled, (state, action) => {
      const fileId = action.payload.file.id;
      const payload = action.payload;
      state.files[fileId] = payload.file;
      state.configs[fileId] = payload.config;
      state.statuses[fileId] = payload.status;
      state.posters[fileId] = payload.poster;
    });
    builder.addCase(updFileUrls.fulfilled, (state, action) => {
      const files = state.files;
      action.payload.forEach((file) => {
        if (files[file.id]) {
          files[file.id].fileWithEffectsUrls = file.fileWithEffectsUrls;
          state.posters[file.id] = file.fileWithEffectsUrls.posterUrl;
        }
      });
    });
    builder.addCase(uploadFilePoster.fulfilled, (state, action) => {
      state.posters[action.payload.fileId] = action.payload.posterURL;
    });
    builder.addCase(passFileAccessedAt.fulfilled, (state, action) => {
      state.files[action.meta.arg].accessedAt = action.payload.data;
    });
    builder.addCase(updateFileMediaOriginDate.pending, (state, action) => {
      state.statuses[action.meta.arg.mediaFileId] = FileStatus.PendingChangingMediaOriginDate;
    });
  },
});

export const { resetFiles, updateFileStatus } = slice.actions;

export default slice.reducer;
